import React from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import {
  formatTeCodePart,
  getTeCodeInfo,
  parseTeCodeParts,
  ORANGE_BOOK_PREFACE_URL,
} from '../utils/teCodeLabels';
import { COLOR_INFO_BG, COLOR_INFO_TEXT } from 'theme/colors';

/** Every tooltip in this feature ends with the same source link (matches TeCodeHeaderTooltip.tsx). */
function buildTooltipContent(bodyText: string): React.ReactNode {
  return (
    <span>
      {bodyText}
      <br />
      Source:{' '}
      <a
        href={ORANGE_BOOK_PREFACE_URL}
        target="_blank"
        rel="noopener"
        style={{ color: 'inherit', textDecoration: 'underline' }}>
        Orange Book Preface
      </a>
    </span>
  );
}

/** Matches InfoTooltip.tsx's own default — one above the modal overlay's zIndex of 10000. */
const DEFAULT_TOOLTIP_Z_INDEX = 10001;

interface TeCodeSubValueProps {
  part: string;
  zIndex: number;
}

/**
 * One sub-code's trigger + tooltip. Split out from `TeCodeValue` (rather
 * than inlined in its `.map()`) so each instance gets its own
 * `portalContainer` state -- a Hook call is only valid per component
 * instance, not per loop iteration of a single component.
 */
const TeCodeSubValue: React.FC<TeCodeSubValueProps> = ({ part, zIndex }) => {
  // Same DOM/tab-order fix as InfoTooltip.tsx: without a `container` next
  // to the trigger, MUI's Popper portals the tooltip (and its Source link)
  // to the end of document.body, unreachable by a plain Tab press.
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);
  const info = getTeCodeInfo(part);
  const display = formatTeCodePart(part);

  return (
    <>
      <MuiTooltip
        title={buildTooltipContent(
          info ? info.fullDescription : 'Code not recognized.'
        )}
        // Without this, MUI overwrites the button's accessible name with
        // the tooltip text (aria-label) instead of describing it (
        // aria-describedby) -- the visible short name would never be
        // announced to screen readers.
        describeChild
        arrow
        placement="top"
        slotProps={{
          popper: { style: { zIndex }, container: portalContainer },
        }}>
        <ButtonBase
          component="span"
          disableRipple
          sx={{
            textDecoration: 'underline dotted',
            cursor: 'help',
            font: 'inherit',
            color: 'inherit',
            borderRadius: '2px',
            '&:hover': {
              backgroundColor: COLOR_INFO_BG,
              color: COLOR_INFO_TEXT,
            },
            '&:active': {
              backgroundColor: COLOR_INFO_BG,
            },
            '&:focus-visible': {
              outline: `2px solid ${COLOR_INFO_TEXT}`,
              outlineOffset: '1px',
            },
          }}>
          {display}
        </ButtonBase>
      </MuiTooltip>
      <span ref={setPortalContainer} style={{ display: 'contents' }} />
    </>
  );
};

export interface TeCodeValueProps {
  /**
   * Raw `te_code` value, possibly a combined string (e.g. `"AB1,AB2,AB3"`).
   * Typed `unknown` (not `string | null`) so an unexpected runtime shape at
   * the JSON-API boundary degrades to a coerced string via `String()`
   * instead of throwing on `.split` — the same tolerance
   * `externalLinkRecordAdapter.ts`'s `formatText` already guarantees for
   * every other field on the same rendering path.
   */
  code: unknown;
  /** Overrides the tooltip popper's z-index for a call site nested above the base 10000 modal overlay (e.g. LinkToSourceModal's 10002 content layer — see ExternalLinkSuggestionsPane.tsx's own 10003 override). */
  zIndex?: number;
}

/**
 * Per-value TE code display (issue #805, acceptance criterion 2):
 * `[code] - [Short name]` with a tooltip carrying the full description.
 * Combined values are split and each sub-code resolved independently;
 * an unrecognized code still gets a tooltip, flagging it as unrecognized
 * and pointing back to the Orange Book Preface source (graceful fallback —
 * the raw code renders either way, never a broken/empty tooltip).
 */
const TeCodeValue: React.FC<TeCodeValueProps> = ({
  code,
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
}) => {
  if (code === null || code === undefined || code === '') return <>—</>;
  const raw = typeof code === 'string' ? code : String(code);
  const parts = parseTeCodeParts(raw);
  if (parts.length === 0) return <>—</>;

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {index > 0 && ', '}
          <TeCodeSubValue part={part} zIndex={zIndex} />
        </React.Fragment>
      ))}
    </>
  );
};

export default TeCodeValue;
